// ---------------------------------------------------------------------------
// FormSection — labelled grouping of form fields (props in → UI out).
//
// Uses Designsystemet's native <fieldset> so assistive tech announces the contained
// controls as one group; the optional `title` becomes the <legend> (the
// group's accessible name), styled as a section heading. `description` adds
// supporting copy below the title. Fields (TextField, Select, Textarea, …)
// are passed as `children` and keep their own intra-form spacing via the
// .field classes.
//
// Keeps layout hooks alongside Designsystemet's component classes:
//   .form-section         — fieldset reset + bottom spacing
//   .form-section__title  — legend, Designsystemet section label
//   .form-section__desc   — muted supporting description
//
// No data fetching, no auth logic, no surface assumptions. Safe for the
// public, sessionless register bundle.
// ---------------------------------------------------------------------------

import { Fieldset, ValidationMessage } from '@digdir/designsystemet-react'
import { useEffect, useId, useRef } from 'react'

type FormSectionProps = {
  /** Stable group target for error-summary links; focusable when enabled. */
  id?: string
  /** Optional heading rendered as the fieldset <legend> (already i18n'd). */
  title?: React.ReactNode
  /** Optional supporting copy shown below the title (already i18n'd). */
  description?: React.ReactNode
  error?: string
  disabled?: boolean
  /** Form fields grouped by this section. */
  children: React.ReactNode
}

/**
 * Presentational form-section wrapper.
 *
 * Usage:
 *   <FormSection title={t('intake.applicant')} description={t('intake.applicant_hint')}>
 *     <TextField … />
 *     <TextField … />
 *   </FormSection>
 */
export function FormSection({ id, title, description, error, disabled, children }: FormSectionProps) {
  const baseId = useId()
  const groupId = id ?? baseId
  const hasTitle = title != null
  const hasDescription = description != null
  const fieldset = useRef<HTMLFieldSetElement>(null)

  useEffect(() => {
    // DS 1.21.1 Fields only watch their own subtree, not a sibling group error.
    // Re-notify their observed ID attribute without replacing controls or owning input ARIA.
    fieldset.current?.querySelectorAll<HTMLElement>('.ds-field .ds-input[id]').forEach(input => {
      if (input.closest('fieldset') === fieldset.current) input.setAttribute('id', input.id)
    })
  }, [error, groupId])

  return (
    <Fieldset
      ref={fieldset}
      id={id}
      disabled={disabled}
      tabIndex={id && !disabled ? -1 : undefined}
      className="form-section"
      // The web layer otherwise includes the paragraph after a legend in the name.
      aria-labelledby={hasTitle ? `${groupId}-legend` : undefined}
      aria-describedby={[hasDescription ? `${groupId}-description` : '', error ? `${groupId}-error` : ''].filter(Boolean).join(' ') || undefined}
      aria-invalid={error ? true : undefined}
    >
      {hasTitle && (
        <Fieldset.Legend id={`${groupId}-legend`} className="form-section__title">{title}</Fieldset.Legend>
      )}

      {hasDescription && (
        <Fieldset.Description id={`${groupId}-description`} className="form-section__desc">{description}</Fieldset.Description>
      )}

      {children}
      {error && <ValidationMessage id={`${groupId}-error`} className="field__err" role="alert">{error}</ValidationMessage>}
    </Fieldset>
  )
}
